import { useState, useCallback } from 'react';
import { zoneOperations } from '../utils/zoneOperations';

interface UseZoneOperationsParams {
  selectedZone: any;
  loadZoneRecords: (zoneId: string) => Promise<void>;
  handleRefresh: () => Promise<void>;
  showToast: (type: 'success' | 'error', message: string) => void;
}

export const useZoneOperations = ({
  selectedZone,
  loadZoneRecords,
  handleRefresh,
  showToast,
}: UseZoneOperationsParams) => {
  const [editingRecord, setEditingRecord] = useState<any>(null);
  const [showNewRecord, setShowNewRecord] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newZone, setNewZone] = useState({
    name: '',
    providerId: '',
    type: 'primary',
  });

  const editRecord = useCallback((record: any) => {
    setEditingRecord(record);
  }, []);

  const addRecord = useCallback(() => {
    setShowNewRecord(true);
  }, []);

  const updateRecord = useCallback(async (recordId: string, updateData: any) => {
    try {
      await zoneOperations.updateRecord(selectedZone, recordId, updateData);
      await loadZoneRecords(selectedZone.id);
      setEditingRecord(null);
      showToast('success', 'DNS record updated successfully');
    } catch (error: any) {
      showToast('error', 'Failed to update DNS record');
    }
  }, [selectedZone, loadZoneRecords, showToast]);

  const deleteRecord = useCallback(async (recordId: string) => {
    if (!confirm('Delete this DNS record?')) return;
    try {
      await zoneOperations.deleteRecord(selectedZone, recordId);
      await loadZoneRecords(selectedZone.id);
      showToast('success', 'DNS record deleted successfully');
    } catch (error: any) {
      showToast('error', 'Failed to delete record');
    }
  }, [selectedZone, loadZoneRecords, showToast]);

  const createRecord = useCallback(async (recordData: any) => {
    try {
      await zoneOperations.createRecord(selectedZone, recordData);
      await loadZoneRecords(selectedZone.id);
      setShowNewRecord(false);
      showToast('success', `Created ${recordData.type} record`);
    } catch (error: any) {
      showToast('error', 'Failed to create DNS record');
    }
  }, [selectedZone, loadZoneRecords, showToast]);

  const bulkDelete = useCallback(async (recordIds: string[]) => {
    try {
      const data = await zoneOperations.bulkDelete(selectedZone, recordIds);
      await loadZoneRecords(selectedZone.id);
      const r = data?.result;
      const msg = r
        ? `Deleted: ${r.successful_operations}/${r.total_operations}`
        : `Deleted ${recordIds.length} records`;
      showToast('success', msg);
    } catch (error: any) {
      showToast('error', 'Bulk delete failed');
    }
  }, [selectedZone, loadZoneRecords, showToast]);

  const bulkUpdateTTL = useCallback(async (recordIds: string[], ttl: number) => {
    try {
      await zoneOperations.bulkUpdateTTL(selectedZone, recordIds, ttl);
      await loadZoneRecords(selectedZone.id);
      showToast('success', 'Updated TTL');
    } catch (error: any) {
      showToast('error', 'Bulk update failed');
    }
  }, [selectedZone, loadZoneRecords, showToast]);

  const addZone = useCallback(async () => {
    try {
      await zoneOperations.addZone(newZone);
      setShowAddModal(false);
      setNewZone({ name: '', providerId: '', type: 'primary' });
      await handleRefresh();
      showToast('success', 'DNS zone added successfully');
    } catch (error: any) {
      showToast('error', 'Failed to add DNS zone');
    }
  }, [newZone, handleRefresh, showToast]);

  const updateNewZone = useCallback((updates: Partial<typeof newZone>) => {
    setNewZone(prev => ({ ...prev, ...updates }));
  }, []);

  return {
    // State
    editingRecord,
    showNewRecord,
    showAddModal,
    newZone,
    
    // Actions
    editRecord,
    addRecord,
    updateRecord,
    deleteRecord,
    createRecord,
    bulkDelete,
    bulkUpdateTTL,
    addZone,
    updateNewZone,
    
    // State setters
    setEditingRecord,
    setShowNewRecord,
    setShowAddModal,
  };
};